import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, Clock, MapPin } from 'lucide-react';
import SEO from './SEO';
import useIsMobile from '../hooks/useIsMobile';

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.8rem 1rem',
  border: '1px solid var(--color-border)',
  borderRadius: '8px',
  fontSize: '0.95rem',
  color: 'var(--color-text)',
  background: 'var(--color-surface)',
  outline: 'none',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '0.4rem',
  fontWeight: 600,
  color: 'var(--color-text-label)',
  fontSize: '0.85rem',
  letterSpacing: '0.02em',
  textTransform: 'uppercase',
};

const options = [
  'Ceiling-Mounted Arm Systems',
  'Mobile Medical Carts',
  'Hospital Workstations',
  'Complete Room Setups',
  'Site Assessment & Planning',
  'Equipment & Installation',
  'Training & Education',
  'Support & Maintenance',
];

const ContactPage: React.FC = () => {
  const isMobile = useIsMobile();
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '', contactDate: '', contactTime: '', timezone: Intl.DateTimeFormat().resolvedOptions().timeZone });
  const [selected, setSelected] = useState<string[]>([]);
  const [otherText, setOtherText] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [focused, setFocused] = useState<string | null>(null);

  const toggleOption = (opt: string) =>
    setSelected(prev => prev.includes(opt) ? prev.filter(o => o !== opt) : [...prev, opt]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    try {
      const res = await fetch('/contact/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, interests: selected.includes('Other') ? [...selected.filter(o => o !== 'Other'), `Other: ${otherText}`] : selected }),
      });
      if (res.ok) { setStatus('success'); window.scrollTo(0, 0); }
      else setStatus('error');
    } catch { setStatus('error'); }
  };

  const focusStyle = (name: string): React.CSSProperties => ({
    ...inputStyle,
    borderColor: focused === name ? 'var(--color-primary)' : 'var(--color-border)',
    boxShadow: focused === name ? '0 0 0 3px rgba(37,99,235,0.1)' : 'none',
  });

  return (
    <>
      <SEO
        title="Contact Us"
        description="Contact JVP Health Tech Solutions to request a quote for healthcare equipment installation, site assessment, or ongoing support. Serving facilities nationwide."
        keywords="contact JVP Health Tech Solutions, request healthcare equipment quote, hospital installation inquiry"
        url="https://www.jvphealthtechsolutions.com/contact"
      />
      <div className="page" style={{ paddingTop: '80px' }}>

        {/* Hero */}
        <div style={{ background: 'linear-gradient(135deg, var(--color-navy) 0%, var(--color-primary) 100%)', color: 'white', padding: isMobile ? '3rem 1.25rem' : '5rem 2rem', textAlign: 'center' }}>
          <h1 style={{ color: 'white', fontSize: isMobile ? '2rem' : '2.8rem', marginBottom: '1rem' }}>Get in Touch</h1>
          <p style={{ fontSize: isMobile ? '1rem' : '1.2rem', maxWidth: '560px', margin: '0 auto', opacity: 0.9, lineHeight: 1.7 }}>
            Tell us about your facility's needs and we'll put together a plan that works for you.
          </p>
        </div>

        {status === 'success' ? (
          <div role="status" style={{ textAlign: 'center', padding: '6rem 2rem' }}>
            <div aria-hidden="true" style={{ width: '72px', height: '72px', background: 'var(--color-success-bg)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', fontSize: '2rem' }}>✓</div>
            <h2 style={{ color: 'var(--color-text)', fontSize: '1.75rem', marginBottom: '0.75rem' }}>Message Received</h2>
            <p style={{ color: 'var(--color-text-muted)', fontSize: '1.05rem', maxWidth: '420px', margin: '0 auto 2.5rem', lineHeight: 1.7 }}>
              A member of our team will reach out to you shortly.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/" style={{ background: 'var(--color-primary)', color: 'white', padding: '0.75rem 2rem', borderRadius: '50px', fontWeight: 600, textDecoration: 'none', fontSize: '0.95rem' }}>Back to Home</Link>
              <Link to="/services" style={{ background: 'white', color: 'var(--color-primary)', padding: '0.75rem 2rem', borderRadius: '50px', fontWeight: 600, textDecoration: 'none', fontSize: '0.95rem', border: '2px solid var(--color-primary)' }}>Our Services</Link>
              <Link to="/work" style={{ background: 'white', color: 'var(--color-primary)', padding: '0.75rem 2rem', borderRadius: '50px', fontWeight: 600, textDecoration: 'none', fontSize: '0.95rem', border: '2px solid var(--color-primary)' }}>Our Work</Link>
            </div>
          </div>
        ) : (
          <div style={{ maxWidth: '1100px', margin: '0 auto', padding: isMobile ? '3rem 1.25rem' : '5rem 2rem', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr', gap: isMobile ? '3rem' : '5rem', alignItems: 'start' }}>

            {/* Left sidebar */}
            <div>
              <h2 style={{ color: 'var(--color-text)', fontSize: '1.3rem', fontWeight: 700, marginBottom: '0.5rem' }}>Contact Information</h2>
              <p style={{ color: 'var(--color-text-muted)', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: '2rem' }}>Reach out directly or fill out the form and we'll get back to you promptly.</p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', marginBottom: '2.5rem' }}>
                {[
                  { icon: Mail, label: 'Email', value: 'info@jvphealthtechsolutions.com', href: 'mailto:info@jvphealthtechsolutions.com' },
                  { icon: Phone, label: 'Phone', value: '(480) 828-5540', href: 'tel:+16024036905' },
                  { icon: MapPin, label: 'Coverage', value: 'Nationwide', href: null },
                  { icon: Clock, label: 'Hours', value: 'Mon–Fri, 8am–5pm', href: null },
                ].map(({ icon: Icon, label, value, href }) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{ background: 'var(--color-primary-tint)', borderRadius: '10px', padding: '0.65rem', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon size={18} color="var(--color-primary)" strokeWidth={2} />
                    </div>
                    <div>
                      {href
                        ? <a href={href} style={{ color: 'var(--color-text)', textDecoration: 'none', fontWeight: 500, fontSize: '0.95rem' }}>{value}</a>
                        : <span style={{ color: 'var(--color-text)', fontWeight: 500, fontSize: '0.95rem' }}>{value}</span>
                      }
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ background: 'linear-gradient(135deg, var(--color-navy-deep) 0%, var(--color-navy) 100%)', borderRadius: '14px', padding: '1.5rem' }}>
                <p style={{ color: 'var(--color-primary-pale)', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem' }}>Already a customer?</p>
                <p style={{ color: 'var(--color-muted-on-dark-light)', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: '1rem' }}>We'd love to hear about your experience.</p>
                <a href="/reviews" style={{ color: 'white', fontWeight: 600, fontSize: '0.9rem', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                  Leave a Review →
                </a>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} style={{ background: 'white', borderRadius: '16px', padding: isMobile ? '2rem 1.25rem' : '2.5rem', boxShadow: '0 4px 24px rgba(0,0,0,0.07)', border: '1px solid var(--color-surface-alt)', display: 'grid', gap: '1.5rem' }}>

              {/* Name + Email */}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1.25rem' }}>
                <div>
                  <label htmlFor="cp-name" style={labelStyle}>Full Name <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                  <input id="cp-name" type="text" name="name" value={formData.name} onChange={handleChange} required placeholder="Jane Smith"
                    style={focusStyle('name')} onFocus={() => setFocused('name')} onBlur={() => setFocused(null)} />
                </div>
                <div>
                  <label htmlFor="cp-email" style={labelStyle}>Email Address <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                  <input id="cp-email" type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="jane@hospital.org"
                    style={focusStyle('email')} onFocus={() => setFocused('email')} onBlur={() => setFocused(null)} />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="cp-phone" style={labelStyle}>Phone Number</label>
                <input id="cp-phone" type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="(555) 000-0000"
                  style={focusStyle('phone')} onFocus={() => setFocused('phone')} onBlur={() => setFocused(null)} />
              </div>

              {/* Interests */}
              <fieldset style={{ border: 'none', margin: 0, padding: 0 }}>
                <legend style={{ ...labelStyle, padding: 0 }}>Areas of Interest <span style={{ color: 'var(--color-text-muted)', fontWeight: 400, textTransform: 'none', letterSpacing: 0, fontSize: '0.8rem' }}>(select all that apply)</span></legend>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.6rem' }}>
                  {[...options, 'Other'].map(opt => (
                    <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', cursor: 'pointer', padding: '0.6rem 0.85rem', borderRadius: '8px', border: `1px solid ${selected.includes(opt) ? 'var(--color-primary)' : 'var(--color-border)'}`, background: selected.includes(opt) ? 'var(--color-primary-tint)' : 'var(--color-surface)', transition: 'all 0.15s', fontSize: '0.875rem', color: selected.includes(opt) ? 'var(--color-primary-text)' : 'var(--color-text-secondary)', fontWeight: selected.includes(opt) ? 600 : 400 }}>
                      <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggleOption(opt)}
                        style={{ accentColor: 'var(--color-primary)', width: '15px', height: '15px', flexShrink: 0 }} />
                      {opt}
                    </label>
                  ))}
                </div>
                {selected.includes('Other') && (
                  <label htmlFor="cp-other" style={{ position: 'absolute', width: '1px', height: '1px', overflow: 'hidden', clip: 'rect(0,0,0,0)' }}>Please describe the other area of interest</label>
                )}
                {selected.includes('Other') && (
                  <input id="cp-other" type="text" placeholder="Please describe..." value={otherText} onChange={e => setOtherText(e.target.value)}
                    style={{ ...focusStyle('other'), marginTop: '0.75rem' }} onFocus={() => setFocused('other')} onBlur={() => setFocused(null)} />
                )}
              </fieldset>

              {/* Preferred contact time */}
              <div>
                <label style={labelStyle}>Preferred Callback Time <span style={{ color: 'var(--color-text-muted)', fontWeight: 400, textTransform: 'none', letterSpacing: 0, fontSize: '0.8rem' }}>(optional)</span></label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <input id="cp-date" type="date" name="contactDate" value={formData.contactDate} onChange={handleChange}
                    min={new Date().toISOString().split('T')[0]}
                    style={focusStyle('date')} onFocus={() => setFocused('date')} onBlur={() => setFocused(null)} />
                  <select id="cp-time" name="contactTime" value={formData.contactTime} onChange={handleChange}
                    style={focusStyle('time')} onFocus={() => setFocused('time')} onBlur={() => setFocused(null)}>
                    <option value="">Select a time</option>
                    {['8:00 AM','9:00 AM','10:00 AM','11:00 AM','12:00 PM','1:00 PM','2:00 PM','3:00 PM','4:00 PM','5:00 PM'].map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="cp-message" style={labelStyle}>Message <span style={{ color: 'var(--color-danger)' }}>*</span></label>
                <textarea id="cp-message" name="message" value={formData.message} onChange={handleChange} required rows={5}
                  placeholder="Tell us about your facility, project scope, or any questions you have..."
                  style={{ ...focusStyle('message'), resize: 'vertical' }} onFocus={() => setFocused('message')} onBlur={() => setFocused(null)} />
              </div>

              {status === 'error' && (
                <p role="alert" style={{ color: 'var(--color-danger-text)', fontSize: '0.875rem', background: 'var(--color-danger-bg)', border: '1px solid var(--color-danger-border)', borderRadius: '8px', padding: '0.75rem 1rem', margin: 0 }}>
                  Something went wrong. Please try again or call us directly at (480) 828-5540.
                </p>
              )}

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
                <button type="submit" disabled={status === 'submitting'}
                  style={{ background: 'linear-gradient(135deg, var(--color-navy) 0%, var(--color-primary) 100%)', color: 'white', padding: '0.9rem 2.5rem', border: 'none', borderRadius: '50px', fontSize: '0.95rem', fontWeight: 700, cursor: status === 'submitting' ? 'not-allowed' : 'pointer', opacity: status === 'submitting' ? 0.7 : 1, letterSpacing: '0.02em' }}>
                  {status === 'submitting' ? 'Sending…' : 'Send Message →'}
                </button>
                <p style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem', margin: 0 }}>We typically respond within 1 business day.</p>
              </div>

            </form>
          </div>
        )}
      </div>
    </>
  );
};

export default ContactPage;
